import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { materialApi, projectApi, teamApi, wbsApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, PencilSimple, Trash, Package, ArrowDown, ArrowUp } from '@phosphor-icons/react';

export default function MaterialsPage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterProject, setFilterProject] = useState('');
  const [filterTeam, setFilterTeam] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    project_id: '', team_id: '', activity_id: '', material_name: '',
    quantity_received: '', quantity_used: '', log_date: '', remarks: ''
  });
  const [formTeams, setFormTeams] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), wbsApi.listActivities()])
      .then(([pRes, aRes]) => { setProjects(pRes.data); setActivities(aRes.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadData = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterTeam) params.team_id = filterTeam;
    const [logsRes, summaryRes] = await Promise.all([
      materialApi.list(params),
      materialApi.summary(filterProject || undefined)
    ]);
    setLogs(logsRes.data);
    setSummary(summaryRes.data);
  }, [filterProject, filterTeam]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (filterProject) teamApi.list(filterProject).then(r => setTeams(r.data));
    else teamApi.list().then(r => setTeams(r.data));
  }, [filterProject]);

  useEffect(() => {
    if (form.project_id) teamApi.list(form.project_id).then(r => setFormTeams(r.data));
    else setFormTeams([]);
  }, [form.project_id]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      project_id: filterProject || '', team_id: filterTeam || '', activity_id: '',
      material_name: '', quantity_received: '', quantity_used: '',
      log_date: new Date().toISOString().split('T')[0], remarks: ''
    });
    setError('');
    setShowModal(true);
  };

  const openEdit = (log) => {
    setEditing(log);
    setForm({
      project_id: log.project_id, team_id: log.team_id, activity_id: log.activity_id,
      material_name: log.material_name,
      quantity_received: log.quantity_received, quantity_used: log.quantity_used,
      log_date: log.log_date || '', remarks: log.remarks || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const data = {
        ...form,
        quantity_received: parseFloat(form.quantity_received) || 0,
        quantity_used: parseFloat(form.quantity_used) || 0
      };
      if (editing) await materialApi.update(editing.id, data);
      else await materialApi.create(data);
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this material entry?')) return;
    try { await materialApi.delete(id); loadData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="materials-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Material Tracking</h1>
          <p className="text-sm text-[#666666]">Track material receipt, usage, and balance</p>
        </div>
        <Button onClick={openCreate} data-testid="add-material-btn">
          <Plus className="w-4 h-4 mr-2" /> New Entry
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select value={filterProject} onChange={(e) => { setFilterProject(e.target.value); setFilterTeam(''); }} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} data-testid="filter-team">
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </div>

      {/* Material Balance Summary */}
      {summary.length > 0 && (
        <Card className="p-3 sm:p-4" data-testid="material-summary">
          <h3 className="text-sm font-semibold text-[#0A0A0A] mb-2">Material Balances</h3>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] text-[#999] uppercase tracking-wider border-b border-[#E0E0E0]">
                  <th className="pb-2 pl-3 sm:pl-0">Material</th>
                  <th className="pb-2 text-right">Received</th>
                  <th className="pb-2 text-right">Used</th>
                  <th className="pb-2 text-right pr-3 sm:pr-0">Balance</th>
                </tr>
              </thead>
              <tbody>
                {summary.map(s => (
                  <tr key={s.material_name} className="border-b border-[#F0F0F0] last:border-0" data-testid={`summary-row-${s.material_name}`}>
                    <td className="py-2 pl-3 sm:pl-0 font-medium text-[#0A0A0A]">{s.material_name}</td>
                    <td className="py-2 text-right text-[#666]">{s.total_received}</td>
                    <td className="py-2 text-right text-[#666]">{s.total_used}</td>
                    <td className={`py-2 text-right pr-3 sm:pr-0 font-semibold ${s.balance < 0 ? 'text-[#FF3333]' : s.balance > 0 ? 'text-[#00C853]' : 'text-[#666]'}`}>
                      {s.balance}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Entries */}
      <div className="space-y-2">
        {logs.length === 0 ? (
          <Card className="p-6 text-center text-[#666666]">No material entries yet</Card>
        ) : logs.map(log => (
          <Card key={log.id} className="p-3 sm:p-4" data-testid={`material-entry-${log.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Package className="w-4 h-4 text-[#002FA7]" />
                  <span className="font-semibold text-sm text-[#0A0A0A]">{log.material_name}</span>
                  <span className="text-[10px] bg-[#E5E5E5] px-1.5 py-0.5 rounded">{log.activity_name}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[#666666]">
                  <span>{log.team_name}</span>
                  <span>{log.project_name}</span>
                  {log.log_date && <span>{log.log_date}</span>}
                </div>
                {log.remarks && <p className="text-xs text-[#999] mt-1 italic">{log.remarks}</p>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-0.5 text-[#00C853]"><ArrowDown className="w-3 h-3" />{log.quantity_received}</span>
                  <span className="flex items-center gap-0.5 text-[#FF3333]"><ArrowUp className="w-3 h-3" />{log.quantity_used}</span>
                  <span className={`font-bold ${log.balance >= 0 ? 'text-[#002FA7]' : 'text-[#FF3333]'}`} data-testid={`balance-${log.id}`}>
                    ={log.balance}
                  </span>
                </div>
                <button onClick={() => openEdit(log)} className="p-1 text-[#666] hover:text-[#002FA7]" data-testid={`edit-material-${log.id}`}><PencilSimple className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(log.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-material-${log.id}`}><Trash className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Form Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Entry' : 'New Material Entry'}>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="material-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Input label="Date" type="date" value={form.log_date} onChange={(e) => setForm({ ...form, log_date: e.target.value })} data-testid="mat-date-input" />
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value, team_id: '' })} required data-testid="mat-project-select">
            <option value="">-- Select Project --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Team" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} required data-testid="mat-team-select">
            <option value="">-- Select Team --</option>
            {formTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
          <Select label="Activity" value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} required data-testid="mat-activity-select">
            <option value="">-- Select Activity --</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.trade} / {a.activity_name}</option>)}
          </Select>
          <Input label="Material Name" value={form.material_name} onChange={(e) => setForm({ ...form, material_name: e.target.value })} required placeholder="e.g. Cement, Steel, Sand" data-testid="mat-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Qty Received" type="number" min="0" step="any" value={form.quantity_received} onChange={(e) => setForm({ ...form, quantity_received: e.target.value })} required data-testid="mat-received-input" />
            <Input label="Qty Used" type="number" min="0" step="any" value={form.quantity_used} onChange={(e) => setForm({ ...form, quantity_used: e.target.value })} required data-testid="mat-used-input" />
          </div>
          {(form.quantity_received || form.quantity_used) && (
            <div className="text-right text-sm font-semibold text-[#002FA7]" data-testid="mat-balance-preview">
              Balance = {((parseFloat(form.quantity_received) || 0) - (parseFloat(form.quantity_used) || 0)).toFixed(2)}
            </div>
          )}
          <Input label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Optional notes" data-testid="mat-remarks-input" />
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-material-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
